import { useState } from "react";
import { LifeBuoy, Loader2, Send, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * ContactAdminCard — the dashboard tile that lets an owner email the admin
 * team with any query. The actual form is a tiny inline modal so we don't
 * pull in another Dialog primitive for a one-shot interaction.
 */
export default function ContactAdminCard({ token }) {
  const [open, setOpen] = useState(false);
  return (
    <section
      data-testid="owner-contact-admin"
      className="mt-8 rounded-3xl border border-[#E5E1D8] bg-white p-5 flex items-center gap-4"
    >
      <div className="w-11 h-11 rounded-xl bg-[#2A3B2C] flex items-center justify-center shrink-0">
        <LifeBuoy className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <h2 className="font-display text-base font-medium text-[#2A3B2C]">
          Need help? Contact the AlKoncierge team
        </h2>
        <p className="text-xs text-[#647466] mt-0.5 leading-relaxed">
          Questions about your listing, sponsorship, or the halal-verified
          badge? Send us a note — we usually reply within a business day.
        </p>
      </div>
      <button
        data-testid="owner-contact-admin-open"
        onClick={() => setOpen(true)}
        className="h-10 px-4 rounded-full bg-[#2A3B2C] hover:bg-[#1f2b21] text-white text-xs font-medium inline-flex items-center gap-1.5"
      >
        <Send className="w-3.5 h-3.5" />
        Contact admin
      </button>
      {open && (
        <ContactAdminModal token={token} onClose={() => setOpen(false)} />
      )}
    </section>
  );
}

function ContactAdminModal({ token, onClose }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await api.post(
        "/owner/contact_admin",
        { subject, message },
        { headers: { "X-Owner-Token": token } }
      );
      if (res.data?.delivered) {
        toast.success("Message sent — we'll be in touch shortly");
      } else {
        // Saved server-side even if Resend was unavailable
        toast.success("Message saved", {
          description:
            "Email delivery was offline so we queued your note. The admin team will see it.",
        });
      }
      onClose();
    } catch (err) {
      toast.error("Couldn't send message", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="owner-contact-modal"
      className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-6"
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={send}
        className="w-full max-w-md bg-white rounded-3xl border border-[#E5E1D8] shadow-2xl p-6"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-lg font-medium text-[#2A3B2C]">
              Contact the AlKoncierge team
            </h3>
            <p className="text-xs text-[#647466] mt-0.5">
              We&apos;ll reply to the email tied to your owner account.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#8A9A8B] hover:text-[#2A3B2C]"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-4 space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#647466]">
              Subject
            </Label>
            <Input
              data-testid="owner-contact-subject"
              required
              minLength={2}
              maxLength={200}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Question about my Halal Verified badge"
              className="h-11 rounded-xl border-[#E5E1D8] focus-visible:ring-[#D95D39] mt-1"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#647466]">
              Message
            </Label>
            <textarea
              data-testid="owner-contact-message"
              required
              minLength={4}
              maxLength={5000}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell us what's on your mind..."
              className="w-full min-h-[140px] rounded-xl border border-[#E5E1D8] p-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D95D39] mt-1"
            />
            <p className="text-[10px] text-[#8A9A8B] mt-1 text-right">
              {message.length}/5000
            </p>
          </div>
        </div>

        <div className="mt-5 flex gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="h-10 px-4 rounded-full bg-white border border-[#E5E1D8] text-[#647466] text-sm font-medium hover:border-[#2A3B2C]"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid="owner-contact-send"
            disabled={sending || subject.trim().length < 2 || message.length < 4}
            className="h-10 px-5 rounded-full bg-[#D95D39] hover:bg-[#C54E2C] disabled:opacity-50 text-white text-sm font-medium inline-flex items-center gap-2"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Send className="w-4 h-4" />
                Send
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
